import {
  css,
  FlattenInterpolation,
  ThemedStyledProps,
} from 'styled-components';
import { bindTombac, BoundTombac } from '../tombac';

export type PropStylingProps<
  CssAttrName extends string,
  Alias extends string = never
> = {
  [Attr in CssAttrName | Alias as `$${Attr}`]?: string;
};

function propToCssValue(
  tombac: BoundTombac,
  attrName: string,
  propValue: string | number | number[] | undefined,
) {
  if (typeof propValue === 'number' || Array.isArray(propValue)) {
    // For compatibility with the old layoutStyles.
    return compatPropToCssValue(tombac, attrName, propValue);
  }
  if (typeof propValue === 'string') {
    return tombac.parse(propValue);
  }
  return propValue;
}

function compatPropToCssValue(
  tombac: BoundTombac,
  attrName: string,
  propValue: string | number | number[] | undefined,
) {
  if (
    typeof propValue === 'number' &&
    ['height', 'gap', 'margin', 'padding', 'width'].find((i) =>
      attrName.toLowerCase().includes(i.toLowerCase()),
    )
  ) {
    return tombac.space(propValue);
  }
  if (Array.isArray(propValue)) {
    return tombac.space(...propValue);
  }
  return propValue?.toString();
}

export type PropStylingInterpolation<P> = FlattenInterpolation<
  ThemedStyledProps<P, any>
>;

export const createPropStyling = <StyleProps extends Record<string, string>>(
  cssAttrNames: readonly string[],
  aliases?: Record<string, string>,
) => {
  return css<StyleProps>`
    ${({ theme, ...props }) => {
      const tombac = bindTombac(theme);

      const result: Record<string, string | undefined> = {};

      for (let propName in props) {
        let attrName = propName.replace('$', '');
        if (aliases?.hasOwnProperty(attrName)) {
          attrName = aliases[attrName as typeof aliases[number]];
        }
        if (cssAttrNames.includes(attrName)) {
          const propValue = props[propName as keyof typeof props];
          result[attrName] = propToCssValue(tombac, attrName, propValue);
        }
      }

      return result;
    }}
  `;
};
